import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MeetSomeoneCard = () => {
  const navigate = useNavigate();
  const [previewUsers, setPreviewUsers] = useState([
    {
      id: 1,
      name: 'Luna_Mystic',
      age: 28,
      sign: '♓',
      compatibility: 89,
      currentTone: 'C Major',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b9a1a9e3?w=100&h=100&fit=crop&crop=center',
      location: 'San Francisco, CA',
      lastActive: '2 hours ago'
    },
    {
      id: 2,
      name: 'StarSeeker',
      age: 32,
      sign: '♌',
      compatibility: 76,
      currentTone: 'G Major',
      avatar: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?w=100&h=100&fit=crop&crop=center',
      location: 'Austin, TX',
      lastActive: '1 hour ago'
    },
    {
      id: 3,
      name: 'CosmicVibes',
      age: 26,
      sign: '♒',
      compatibility: 82,
      currentTone: 'D Minor',
      avatar: 'https://images.pixabay.com/photo/2016/11/21/12/42/beard-1845166_1280.jpg?w=100&h=100&fit=crop&crop=center',
      location: 'Portland, OR',
      lastActive: '30 minutes ago'
    }
  ]);

  const [nearbyCount, setNearbyCount] = useState(47);
  const [newMatchesCount, setNewMatchesCount] = useState(3);

  const handleNavigateToMeet = () => {
    navigate('/meet-someone-new');
  };

  const getCompatibilityColor = (compatibility) => {
    if (compatibility >= 80) return 'text-success';
    if (compatibility >= 60) return 'text-accent';
    return 'text-text-secondary';
  };

  const getCompatibilityBg = (compatibility) => {
    if (compatibility >= 80) return 'bg-success/20';
    if (compatibility >= 60) return 'bg-accent/20';
    return 'bg-surface/50';
  };

  return (
    <div className="glass bg-surface/50 backdrop-blur-cosmic rounded-2xl p-6 shadow-elevation-3">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-text-primary">
            Meet Someone New
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Compatible souls in your cosmic vicinity
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm font-mono text-accent">
            {nearbyCount} nearby
          </div>
          {newMatchesCount > 0 && (
            <div className="text-xs text-success">
              {newMatchesCount} new matches
            </div>
          )}
        </div>
      </div>

      {/* Preview Users */}
      <div className="space-y-3 mb-6">
        {previewUsers.map((user) => (
          <div
            key={user.id}
            className="flex items-center space-x-3 p-3 glass bg-surface/30 backdrop-blur-sm rounded-xl transition-cosmic hover:bg-surface/50 cursor-pointer"
            onClick={() => console.log('View user profile:', user.name)}
          >
            {/* Avatar */}
            <div className="relative">
              <img
                src={user.avatar}
                alt={`${user.name} avatar`}
                className="w-12 h-12 rounded-full object-cover"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                }}
              />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                <span className="text-xs text-primary-foreground">{user.sign}</span>
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="text-sm font-heading font-medium text-text-primary truncate">
                  {user.name}
                </h3>
                <span className="text-xs text-text-secondary">
                  {user.age}
                </span>
              </div>
              <div className="flex items-center space-x-2 mt-1">
                <Icon name="MapPin" size={12} color="currentColor" />
                <span className="text-xs text-text-secondary truncate">
                  {user.location}
                </span>
              </div>
              <div className="flex items-center space-x-2 mt-1">
                <Icon name="Music" size={12} color="#F59E0B" />
                <span className="text-xs font-mono text-accent">
                  {user.currentTone}
                </span>
              </div>
            </div>

            {/* Compatibility */}
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getCompatibilityBg(user.compatibility)}`}>
                <span className={`text-sm font-bold ${getCompatibilityColor(user.compatibility)}`}>
                  {user.compatibility}%
                </span>
              </div>
              <div className="text-xs text-text-secondary mt-1">
                {user.lastActive}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 glass bg-surface/30 backdrop-blur-sm rounded-xl">
          <div className="text-lg font-heading font-semibold text-primary">
            {nearbyCount}
          </div>
          <div className="text-xs text-text-secondary">Nearby</div>
        </div>
        <div className="text-center p-3 glass bg-surface/30 backdrop-blur-sm rounded-xl">
          <div className="text-lg font-heading font-semibold text-success">
            {newMatchesCount}
          </div>
          <div className="text-xs text-text-secondary">New Matches</div>
        </div>
        <div className="text-center p-3 glass bg-surface/30 backdrop-blur-sm rounded-xl">
          <div className="text-lg font-heading font-semibold text-accent">
            87%
          </div>
          <div className="text-xs text-text-secondary">Avg Match</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          variant="primary"
          iconName="Users"
          onClick={handleNavigateToMeet}
          fullWidth
        >
          Start Meeting People
        </Button>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            iconName="Filter"
            onClick={() => console.log('Filter preferences')}
            fullWidth
          >
            Filters
          </Button>
          <Button
            variant="outline"
            iconName="Settings"
            onClick={() => console.log('Match settings')}
            fullWidth
          >
            Settings
          </Button>
        </div>
      </div>

      {/* Astrological Insight */}
      <div className="mt-6 pt-4 border-t border-border/50">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center animate-gentle-pulse">
            <Icon name="Sparkles" size={16} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-heading font-medium text-text-primary">
              Today's Connection Insight
            </h4>
            <p className="text-xs text-text-secondary mt-1">
              Venus in your 7th house suggests heightened romantic energy. Perfect time for meaningful connections with water signs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetSomeoneCard;